import React from "react"

import { formatNumber } from "accounting"
import { Flex, Typography } from "antd"
import ReactCountryFlag from "react-country-flag"

import { countryCodesByCountry } from "../../../../Components/Filters/filterOptionsData"
import Private from "../../../../Components/Private"
import Images from "../../../../Images"
import AppUtils, { formatCurrency, serviceImgTooltip, serviceMap } from "../../../../Utils/utils"

const { Text } = Typography

const baseColumn = [
  {
    title: "Count",
    dataIndex: "count",
    key: "count",
    width: "80px",
    align: "right",
    render: (_) => formatNumber(_)
  },
  {
    title: "Volume ($)",
    dataIndex: "volume",
    key: "volume",
    width: "120px",
    align: "right",
    render: (_) => <Private>{`$${formatNumber(_, 2, ",")}`}</Private>
  }
]

export const alertsByBankColumns = [
  {
    title: "Bank name",
    dataIndex: "label",
    key: "label",
    render: (_) => (_ ? <Private>{_}</Private> : "N/A")
  },
  ...baseColumn
]

// The detail table on the Alerts by Bank page. Separate from alertsByBankColumns because that
// set also feeds the small card on the dashboard, which has no room for these extra columns.
export const alertsByBankDetailColumns = [
  {
    title: "Bank name",
    dataIndex: "label",
    key: "label",
    width: 200,
    render: (label) => <Text ellipsis={{ tooltip: label }}>{label || "N/A"}</Text>
  },
  {
    title: "Count",
    dataIndex: "count",
    key: "count",
    width: 120,
    align: "right",
    render: (_) => formatNumber(_)
  },
  {
    title: "Volume ($)",
    dataIndex: "volume",
    key: "volume",
    width: 160,
    align: "right",
    render: (_) => <Private>{`$${formatNumber(_, 2, ",")}`}</Private>
  },
  {
    title: "Average alert value",
    dataIndex: "average",
    key: "average",
    width: 180,
    align: "right",
    render: (_) => <Private>{`$${formatNumber(_ ?? 0, 2, ",")}`}</Private>
  },
  {
    title: "Share",
    dataIndex: "percentage",
    key: "percentage",
    width: 110,
    align: "right",
    render: (_) => `${formatNumber(_ ?? 0, 1, ",", ".")}%`
  }
]

export const alertsByCardTypeColumns = [
  {
    title: "Card type",
    dataIndex: "label",
    key: "label",
    render: (_) => (
      <Flex gap={12}>
        {Images[_?.toLowerCase()] && (
          <img src={Images[_?.toLowerCase()]} alt={_} width={24} height={24} />
        )}
        {_ || "N/A"}
      </Flex>
    )
  },
  ...baseColumn
]

export const usersBySourceTypeColumns = [
  {
    title: "Source",
    dataIndex: "label",
    key: "label",
    render: (_) => <Flex gap={12}>{AppUtils.toUpperLatter(_) || "N/A"}</Flex>
  },
  baseColumn[0]
]

export const alertsByCountryColumns = [
  {
    title: "Country",
    dataIndex: "label",
    key: "label",
    width: 200,
    render: (label) => {
      const code = countryCodesByCountry[label]
      return (
        <Flex align="center" gap={8} className="country-cell">
          {code && (
            <ReactCountryFlag
              countryCode={code}
              svg
              style={{ width: 20, height: 15, borderRadius: 4, flexShrink: 0 }}
              aria-hidden
            />
          )}
          <Text ellipsis={{ tooltip: label }}>{label || "N/A"}</Text>
        </Flex>
      )
    }
  },
  // Fixed widths on the three numeric columns. Without them the fixed table layout splits the
  // width four ways and the figures drift apart from each other across the row.
  {
    title: "Count",
    dataIndex: "count",
    key: "count",
    width: 120,
    align: "right",
    render: (_) => formatNumber(_)
  },
  {
    title: "Volume ($)",
    dataIndex: "volume",
    key: "volume",
    width: 160,
    align: "right",
    render: (_) => <Private>{`$${formatNumber(_, 2, ",")}`}</Private>
  },
  {
    title: "Average alert value",
    dataIndex: "average",
    key: "average",
    width: 180,
    align: "right",
    render: (_) => <Private>{`$${formatNumber(_ ?? 0, 2, ",")}`}</Private>
  },
  {
    title: "Share",
    dataIndex: "percentage",
    key: "percentage",
    width: 110,
    align: "right",
    render: (_) => `${formatNumber(_ ?? 0, 1, ",", ".")}%`
  }
]

export const getDashboardProgressRowProps = (record) => ({
  className: "dashboard-progress-row",
  style: {
    "--dashboard-row-progress": `${record?.percentage || 0}%`
  }
})

// Why each credit was raised. Both this and the source breakdown count approved credits, so
// the two columns partition the same population and their amounts add up to Fees recovered.
export const creditTriggersColumns = [
  {
    title: "Trigger",
    dataIndex: "label",
    key: "label",
    ellipsis: true,
    render: (_) => <Text ellipsis>{AppUtils.toUpperLatter(_) || "N/A"}</Text>
  },
  {
    ...baseColumn[0],
    title: "Credits"
  },
  {
    ...baseColumn[1],
    title: "Amount ($)",
    render: (_) => <Private>{formatCurrency(_ ?? 0, true)}</Private>
  }
]

// Which network the approved credits came back through. `label` is the raw alert_service.
export const creditsBySourceColumns = (theme) => [
  {
    title: "Source",
    dataIndex: "label",
    key: "label",
    render: (_) => {
      const logo = Images[serviceImgTooltip?.[theme]?.[_]]
      return (
        <Flex gap={8} align="center">
          {logo ? <img src={logo} width={20} height={20} alt={`${_} logo`} /> : null}
          <Text>{serviceMap[_] || AppUtils.toUpperLatter(_) || "N/A"}</Text>
        </Flex>
      )
    }
  },
  {
    ...baseColumn[0],
    title: "Credits"
  },
  {
    ...baseColumn[1],
    title: "Amount ($)",
    render: (_) => <Private>{formatCurrency(_ ?? 0, true)}</Private>
  }
]
